import React, { useState, useEffect } from 'react';
import { Card, Box, Stack, Typography, Button, useTheme, IconButton } from '@mui/material';
import { WarningAmber, Fullscreen, FullscreenExit } from '@mui/icons-material';
import { Task } from './types';

export function UrgentAlertCard({ task }: { task: Task }) {
  const theme = useTheme();
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isFullscreen]);

  return (
    <Card sx={{
      position: 'relative',
      overflow: 'hidden',
      borderRadius: 3,
      minHeight: 260,
      height: '100%',
      display: 'flex',
      alignItems: 'stretch',
      //backgroundColor: theme.palette.common.white,
      ...(isFullscreen && {
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        minHeight: 'auto',
        zIndex: 1300,
        borderRadius: 0,
      }),
    }}>
      {/* Background image layer */}
      {task.backgroundUrl ? (
        <Box sx={{
          position: 'absolute', inset: 0,
          backgroundImage: `url(${task.backgroundUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'brightness(0.9)',
        }} />
      ) : null}
      {/* Dark overlay */}
      <Box sx={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)' }} />

      {/* Urgent pill */}
      <Box sx={{
        position: 'absolute', top: 16, left: 16,
        display: 'inline-flex', alignItems: 'center', gap: 0.75,
        px: 6, py: 3,
        fontSize: 14, fontWeight: 600,
        borderRadius: 2,
        background: 'rgba(255, 214, 94, 0.5)',
        color: theme.palette.common.white,
        border: '1px solid rgba(255, 214, 94, 0.95)',
        backdropFilter: 'blur(4px)',
        zIndex: 2,
      }}>
        <WarningAmber sx={{ fontSize: 26, pr: '5px', color: theme.palette.common.black }} />
        Urgent Task
      </Box>

      {/* Fullscreen toggle */}
      <IconButton
        aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
        onClick={() => setIsFullscreen(f => !f)}
        sx={{
          position: 'absolute', top: 8, right: 8, zIndex: 2,
          backgroundColor: 'rgba(0,0,0,0.4)',
          color: 'white',
          '&:hover': { backgroundColor: 'rgba(0,0,0,0.6)' },
        }}
        size="small"
      >
        {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
      </IconButton>

      {/* Content */}
      <Box sx={{
        position: 'relative',
        zIndex: 1,
        width: '100%',
        height: '100%',
        paddingTop: '100px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        px: { xs: 3, md: 8 },
        py: { xs: 8, md: 10 },
      }}>
        <Stack spacing={2}
          sx={{ maxWidth: 760, width: '100%', height: '100%', marginTop: '50px', justifyContent: 'space-evenly' }}>
          <Typography component="h4"
            sx={{ fontWeight: 700, color: 'white', lineHeight: 1.15, fontSize: { xs: 28, md: 36 } }}>
            {task.title}
          </Typography>
          {task.body && (
            <Typography sx={{ fontSize: { xs: 14, md: 16 }, fontWeight: 600, color: 'white', lineHeight: 1.4 }}>
              {task.body}
            </Typography>
          )}
          {task.secondaryText && (
            <Typography sx={{ fontSize: { xs: 13, md: 15 }, color: 'rgba(255,255,255,0.75)', lineHeight: 1.35 }}>
              {task.secondaryText}
            </Typography>
          )}
          {(task.ctaText && (task.ctaHref)) && (
            <Box pt={1}>
              <Button
                href={task.ctaHref}
                sx={{
                  backgroundColor: '#F2B8AD',
                  color: '#1a1a1a',
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: 14,
                  height: 44,
                  px: 3.5,
                  py: 1.25,
                  borderRadius: 1.5,
                  boxShadow: 'none',
                  '&:hover': { backgroundColor: '#e8a79c', boxShadow: 'none' },
                }}>
                {task.ctaText}
              </Button>
            </Box>
          )}
        </Stack>
      </Box>
    </Card>
  );
}
